// src/components/Consent/AnalyticsGate.js
import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { getCookie } from "../../Utils/Cookies";

const GA_ID = String(process.env.REACT_APP_GA4_MEASUREMENT_ID || "").trim();

const GA_SCRIPT_ID = "seho-ga4-script";
const GA_CONFIGURED_FLAG = "__sehoGa4Configured";

const GA_ALLOWED_HOSTNAMES = new Set(["sehopro.com", "www.sehopro.com"]);

function isAllowedAnalyticsHostname() {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = String(window.location.hostname || "")
    .trim()
    .toLowerCase();

  return GA_ALLOWED_HOSTNAMES.has(hostname);
}

function ensureGtagQueue() {
  window.dataLayer = window.dataLayer || [];

  if (typeof window.gtag !== "function") {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

function loadGoogleTagScript() {
  if (!GA_ID || document.getElementById(GA_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement("script");

  script.id = GA_SCRIPT_ID;
  script.async = true;
  script.src =
    "https://www.googletagmanager.com/" +
    `gtag/js?id=${encodeURIComponent(GA_ID)}`;

  document.head.appendChild(script);
}

function getSavedConsent(consentDetail = null) {
  const savedAnalytics = getCookie("seho_cc_analytics") === "1";

  const savedMarketing = getCookie("seho_cc_marketing") === "1";

  return {
    analytics:
      typeof consentDetail?.analytics === "boolean"
        ? consentDetail.analytics
        : savedAnalytics,

    marketing:
      typeof consentDetail?.marketing === "boolean"
        ? consentDetail.marketing
        : savedMarketing,
  };
}

export default function AnalyticsGate() {
  const location = useLocation();

  const [trackingReady, setTrackingReady] = useState(false);

  const lastPageKeyRef = useRef("");

  useEffect(() => {
    if (
      !GA_ID ||
      typeof window === "undefined" ||
      !isAllowedAnalyticsHostname()
    ) {
      return undefined;
    }

    ensureGtagQueue();

    /*
     * Deny optional Google storage until
     * the visitor's saved consent has been
     * read and applied.
     */
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
      wait_for_update: 500,
    });

    const applyConsent = (consentDetail = null) => {
      const { analytics, marketing } = getSavedConsent(consentDetail);

      const disableFlag = `ga-disable-${GA_ID}`;

      /*
       * Google Analytics also checks this
       * global flag before sending hits.
       */
      window[disableFlag] = !analytics;

      window.gtag("consent", "update", {
        analytics_storage: analytics ? "granted" : "denied",

        ad_storage: marketing ? "granted" : "denied",

        ad_user_data: marketing ? "granted" : "denied",

        ad_personalization: marketing ? "granted" : "denied",
      });

      if (!analytics) {
        lastPageKeyRef.current = "";
        setTrackingReady(false);
        return;
      }

      loadGoogleTagScript();

      /*
       * Configure GA4 once.
       *
       * send_page_view is disabled because
       * React Router page views are sent
       * manually below.
       */
      if (!window[GA_CONFIGURED_FLAG]) {
        window.gtag("js", new Date());

        window.gtag("config", GA_ID, {
          send_page_view: false,
        });

        window[GA_CONFIGURED_FLAG] = true;
      }

      setTrackingReady(true);
    };

    applyConsent();

    const onConsentChanged = (event) => {
      applyConsent(event?.detail || null);
    };

    window.addEventListener("seho:consentChanged", onConsentChanged);

    return () => {
      window.removeEventListener("seho:consentChanged", onConsentChanged);
    };
  }, []);

  useEffect(() => {
    if (
      !GA_ID ||
      !trackingReady ||
      !isAllowedAnalyticsHostname() ||
      typeof window.gtag !== "function"
    ) {
      return undefined;
    }

    const pageKey = `${location.pathname}` + `${location.search}`;

    /*
     * Allow SeoManager to finish updating
     * the document title before GA reads it.
     */
    const timer = window.setTimeout(() => {
      if (lastPageKeyRef.current === pageKey) {
        return;
      }

      window.gtag("event", "page_view", {
        page_title: document.title,

        page_location: window.location.href,

        page_path: pageKey,
      });

      lastPageKeyRef.current = pageKey;
    }, 0);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.search, trackingReady]);

  return null;
}
